<article
  class="slot-card"
  [class.slot-fulfilled]="isFulfilled()"
  [class.slot-missing]="!isFulfilled() && !isOtherSlot() && !isSkipped()"
  [class.slot-skipped]="isSkipped()"
  [class.slot-other]="isOtherSlot() && !isFulfilled()"
>
  <input #fileInput type="file" class="hidden-file-input" (change)="onFileSelected($event)" />

  <header class="slot-head">
    <div class="slot-head-text">
      <span class="slot-title">{{ this.evidence().name }}</span>
    </div>
    <span
      class="slot-scope"
      title="{{
        'requirementIntake.evidence.scopeDescriptions.' + this.evidence().scope | translate
      }}"
    >
      {{ 'requirementIntake.evidence.scope' | translate }}
      {{ 'model.evidence.scope.' + this.evidence().scope | translate }}
    </span>
    @if (isSkipped()) {
      <!-- no badge shown — skip chip below communicates the state -->
    } @else if (isConfirmReuse()) {
      <span class="slot-status slot-status-confirm-reuse">
        {{ 'requirementIntake.evidence.slot.status.confirmReuse' | translate }}
      </span>
    } @else if (isFulfilled()) {
      <span class="slot-status slot-status-provided">
        &#10003; {{ 'requirementIntake.evidence.slot.status.provided' | translate }}
      </span>
    } @else if (isOtherSlot()) {
      <span class="slot-status slot-status-optional">{{
        'requirementIntake.evidence.slot.status.optional' | translate
      }}</span>
    } @else {
      <span class="slot-status slot-status-missing">
        &#33; {{ 'requirementIntake.evidence.slot.status.missing' | translate }}
      </span>
    }
  </header>
  @if (!isSkipped()) {
    <comp-description
      [description]="this.evidence().description"
      [showMaxLines]="3"
    ></comp-description>
  }

  @if (isSkipped()) {
    <!-- Compact skip chip with undo -->
    <div class="skip-chip">
      <span class="skip-chip-icon" aria-hidden="true">&#8856;</span>
      <span class="skip-chip-text">{{
        'requirementIntake.evidence.slot.status.notProvided' | translate
      }}</span>
      <button type="button" class="skip-undo" (click)="undoSkip()">
        {{ 'requirementIntake.evidence.slot.actions.provideAnyway' | translate }}
      </button>
    </div>
  } @else {
    @if (currentUpload()) {
      <comp-file-status
        [file]="currentUpload()!"
        [uploadPercentage]="uploadPercentage()"
      ></comp-file-status>
    }
    @if (evidence()?.fileAttachments?.length) {
      <!-- File chip when evidence is already attached -->
      @for (file of evidence()?.fileAttachments; track file.id) {
        <comp-file-status
          [file]="file"
          (onDelete)="handleDelete($event)"
          (onReuseConfirmed)="handleReuseConfirmed()"
        ></comp-file-status>
      }
    }
    @if (interactionMessage(); as msg) {
      <p class="{{ msg.isError ? 'ul-inline-alert -critical' : 'info' }}">{{ msg.message }}</p>
    }
    <!-- Collapsed toggle to add more -->
    <button
      type="button"
      class="add-more-toggle"
      (click)="toggleAnotherFileForm()"
      [attr.aria-expanded]="isAddAnotherFileOpen()"
    >
      @if (!currentUpload()) {
        @if ((isFulfilled() || isOtherSlot()) && isAddAnotherFileOpen()) {
          {{ 'requirementIntake.evidence.slot.actions.hideUploadOptions' | translate }}
        } @else if (!isAddAnotherFileOpen()) {
          @if (isOtherSlot() && !isFulfilled()) {
            {{ 'requirementIntake.evidence.slot.actions.provideOtherEvidence' | translate }}
          } @else {
            {{ 'requirementIntake.evidence.slot.actions.provideMoreEvidence' | translate }}
          }
        }
      }
    </button>

    @if (isAddAnotherFileOpen()) {
      <div
        class="dropzone"
        [class.dropzone-drag-active]="isDragActive()"
        role="button"
        tabindex="0"
        [attr.aria-label]="
          'requirementIntake.evidence.slot.aria.uploadFor'
            | translate: { name: this.evidence().name }
        "
        [attr.aria-disabled]="currentUpload()"
        (click)="requestUpload()"
        (keydown)="onDropzoneKeydown($event)"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        <span class="dropzone-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
        </span>
        <span class="dropzone-body">
          <span class="dropzone-title">
            {{ 'requirementIntake.evidence.slot.upload.dragDropOr' | translate }}
            <button
              type="button"
              class="browse-link"
              (click)="$event.stopPropagation(); requestUpload()"
            >
              {{ 'requirementIntake.evidence.slot.upload.browse' | translate }}
            </button>
          </span>
          <span class="dropzone-hint">{{
            'requirementIntake.evidence.slot.upload.hint' | translate
          }}</span>
        </span>
      </div>

      <div class="method-tiles">
        <button type="button" class="method-tile" (click)="openReuseDialog()">
          <span class="method-tile-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="1 4 1 10 7 10"></polyline>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
            </svg>
          </span>
          <span class="method-tile-body">
            <span class="method-tile-title">{{
              'requirementIntake.evidence.slot.methods.reuse.title' | translate
            }}</span>
            <span class="method-tile-hint">{{
              'requirementIntake.evidence.slot.methods.reuse.hint' | translate
            }}</span>
          </span>
        </button>

        <button
          type="button"
          class="method-tile"
          (click)="openTextEvidence()"
          [attr.aria-expanded]="isTextEntryOpen()"
        >
          <span class="method-tile-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="4 7 4 4 20 4 20 7"></polyline>
              <line x1="9" y1="20" x2="15" y2="20"></line>
              <line x1="12" y1="4" x2="12" y2="20"></line>
            </svg>
          </span>
          <span class="method-tile-body">
            <span class="method-tile-title">{{
              'requirementIntake.evidence.slot.methods.text.title' | translate
            }}</span>
            <span class="method-tile-hint">{{
              'requirementIntake.evidence.slot.methods.text.hint' | translate
            }}</span>
          </span>
        </button>
      </div>

      @if (!isFulfilled()) {
        <button type="button" class="skip-link" (click)="markNotProvided()">
          {{ 'requirementIntake.evidence.slot.actions.markNotProvided' | translate }}
        </button>
      }
    }
  }

  <!-- Animated text evidence textarea - available for both fulfilled and empty slots -->
  @if (!isSkipped()) {
    <div
      class="slot-textarea-wrap"
      [class.slot-textarea-wrap-open]="isTextEntryOpen()"
      [attr.aria-hidden]="!isTextEntryOpen()"
    >
      <label class="text-evidence-label" [for]="'textEvidence-' + this.evidence().id">{{
        'requirementIntake.evidence.slot.text.label' | translate
      }}</label>
      <textarea
        [compAutofocus]="isTextEntryOpen()"
        [id]="'textEvidence-' + this.evidence().id"
        [disabled]="!isTextEntryOpen()"
        [value]="textEvidenceDraft()"
        (input)="textEvidenceDraft.set($any($event.target).value)"
        rows="3"
        [placeholder]="'requirementIntake.evidence.slot.text.placeholder' | translate"
      ></textarea>
      <div class="slot-textarea-actions">
        <button comp-button="tertiary" type="button" (click)="cancelTextEvidence()">
          {{ 'general.cancel' | translate }}
        </button>
        <button comp-button action="save" type="button" (click)="saveTextEvidence()">
          {{ 'requirementIntake.evidence.slot.text.save' | translate }}
        </button>
      </div>
    </div>
  }
</article>
